import type { ComponentPublicInstance, Ref } from 'vue'

/**
 * Resolves where a PrimeVue overlay panel (Dropdown / MultiSelect / …) should be
 * teleported to.
 *
 * PrimeVue defaults to appending overlay panels to `document.body`. But
 * `AppModalCard` opens via `<dialog>.showModal()`, which promotes the modal into
 * the browser's *top layer* — and anything appended to `body` renders *beneath*
 * the top layer, so the panel becomes invisible/unclickable inside a modal.
 * (Appending to the trigger itself — `appendTo="self"` — instead gets clipped by
 * the overflow-hidden control.)
 *
 * Appending the panel into the enclosing `<dialog>` keeps it in the same top
 * layer as the modal, so it paints above the scrim and escapes the clip. When
 * the control isn't inside a dialog, this falls back to PrimeVue's default of
 * `'body'`.
 *
 * Pass a template ref to the PrimeVue component (or a raw element); the target is
 * resolved once on mount and returned as a ref to bind to `:append-to`.
 */
export function useOverlayAppendTarget(
  elRef: Ref<ComponentPublicInstance | HTMLElement | null | undefined>,
): Ref<HTMLElement | 'body'> {
  const appendTarget = ref<HTMLElement | 'body'>('body')

  onMounted(() => {
    const raw = elRef.value
    const el =
      raw instanceof HTMLElement
        ? raw
        : ((raw as ComponentPublicInstance | undefined)?.$el as
            | HTMLElement
            | undefined)
    appendTarget.value = el?.closest('dialog') ?? 'body'
  })

  return appendTarget
}
